import JsPDF, { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import html2canvas from "html2canvas";
import { cloneDeep, head } from "lodash";
import quillSnowCss from "quill/dist/quill.snow.css?inline";
applyPlugin(JsPDF);
(function (jsPDFAPI) {
  // 1) ZWJ + special-character helper
  const zwj = "\u200D";
  const specials = ["\u06A8", "\u0651"]; // ڨ (GAF), SHADDA

  function fixArabicLigatures(s) {
    let out = "";
    for (let i = 0; i < s.length; i++) {
      out += s[i];
      if (
        s[i + 1] &&
        (specials.includes(s[i]) || specials.includes(s[i + 1]))
      ) {
        out += zwj;
      }
    }
    return out;
  }

  // 2) Shaping wrapper
  function shapeIfArabic(txt) {
    // Only shape if there’s at least one Arabic code-point
    if (!/[\u0600-\u06FF]/.test(txt)) {
      return txt;
    }
    // Make sure the plugin is there
    if (typeof jsPDFAPI.processArabic !== "function") {
      console.error("Arabic plugin missing! Include modules/arabic.js first.");
      return txt;
    }
    // Reverse for RTL + shape
    return jsPDFAPI.processArabic(fixArabicLigatures(txt), true);
  }

  // 3) Register your shaper on every call to doc.text()
  jsPDFAPI.events.push([
    "preProcessText",
    function (originalText /*, renderOptions */) {
      // return the (possibly transformed) string
      return shapeIfArabic(originalText);
    },
  ]);
})(jsPDF.API);
function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ]
    : null;
}
function getMMDDYYY(date) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) throw new Error("invalid date");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return { mm, dd, yyyy };
  } catch (error) {
    return { mm: "--", dd: "--", yyyy: "--" };
  }
}

function formatBirthDateYmd(value) {
  if (value == null || value === "") return "";
  const { yyyy, mm, dd } = getMMDDYYY(value);
  if (yyyy === "--") return "";
  // Non-breaking hyphens keep YYYY-MM-DD on one line in narrow table cells.
  return `${yyyy}\u2011${mm}\u2011${dd}`;
}

// Student records store gender as the English "male"/"female"; the table must
// display the Arabic labels. Unknown values fall through unchanged.
function formatGender(value) {
  if (value === "male") return "\u0630\u0643\u0631";
  if (value === "female") return "\u0623\u0646\u062b\u0649";
  return value || "";
}

function findYearsPerLevel(studentID, levelData, maxLevel = 6) {
  // NB: `typeof [] === "object"`, never "array" — the previous Array check was
  // always false, so this always fell back to [1,1,1,1,1,1] and every student
  // showed all 1s regardless of the API data.
  const list = Array.isArray(levelData) ? levelData : [];
  const student = list.find((levelDataItem) => levelDataItem.id == studentID);
  const levels = student?.years ?? [1, 1, 1, 1, 1, 1];
  return {
    1: levels[0],
    2: maxLevel >= 2 ? levels[1] : "--",
    3: maxLevel >= 3 ? levels[2] : "--",
    4: maxLevel >= 4 ? levels[3] : "--",
    5: maxLevel >= 5 ? levels[4] : "--",
    6: maxLevel >= 6 ? levels[5] : "--",
  }
}

// Canonical, complete admis-table column order. Used to reconcile a persisted
// `columnsOrder` that predates some columns so an enabled-but-missing column
// (e.g. "gender") still renders instead of being silently dropped.
const ADMIN_TABLE_COLUMN_ORDER = [
  "number",
  "fullName",
  "firstName",
  "lastName",
  "gender",
  "birthDate",
  "birthPlace",
  "uniqueIden",
  "prevYears",
  "trim1",
  "trim2",
  "trim3",
  "moy",
  "decision",
  "handicape",
];

const PV_TABLE_HEAD_FONT = 6;
const PV_TABLE_BODY_FONT = 8;
// Larger font used for the Birth date and Decision (يرتقي) cells so they make
// the most of the space available in the last columns.
const PV_TABLE_WIDE_FONT = 9;
const PV_TABLE_CELL_PADDING = 1;

function formatTableLabel(text) {
  return String(text ?? "")
    .trim()
    .split("\n")
    .map((line) => line.trim().replace(/ +/g, "\u00A0"))
    .join("\n");
}

function formatTableCellText(text) {
  const value = String(text ?? "");
  if (!value || !/[\u0600-\u06FF]/.test(value)) return value;
  return value.replace(/\s+/g, "\u00A0");
}

function splitLabelAtWordBoundaries(doc, text, maxWidth, fontSize) {
  const normalized = String(text).replace(/\u00A0/g, " ").trim();
  if (!normalized) return [""];
  doc.setFont("Amiri");
  doc.setFontSize(fontSize);
  if (doc.getTextWidth(normalized) <= maxWidth) return [normalized];
  const words = normalized.split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && doc.getTextWidth(candidate) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [normalized];
}

function formatHeadCell(cell) {
  if (cell == null || cell === "") return cell;
  if (typeof cell === "string") return formatTableLabel(cell);
  if (typeof cell === "object" && cell.content != null) {
    return { ...cell, content: formatTableLabel(cell.content) };
  }
  return cell;
}

// Resolves an explicit, user-configured font size for a column from
// config.table.fontSizes. Entries look like { auto, size }; a fixed size is
// only honored when `auto` is falsy and a `size` is set (see the bulletin UI's
// saveTableModalData). The prevYears column is expanded into the "1".."6"
// sub-columns, which all share the "prevYears" font-size entry.
function resolveColumnFixedFontSize(fontSizes, columnKey) {
  if (!fontSizes) return null;
  const key = ["1", "2", "3", "4", "5", "6"].includes(columnKey)
    ? "prevYears"
    : columnKey;
  const entry = fontSizes[key];
  if (entry && entry.size && !entry.auto) return entry.size;
  return null;
}

function buildMainTableColumnStyles(expandedColumns, tableWidth, fontSizes) {
  const styles = {};
  const weightFor = (key) => {
    if (key === "number") return 0.5;
    if (key === "fullName") return 2.3;
    if (key === "firstName" || key === "lastName") return 1.5;
    if (key === "birthDate") return 2.4;
    if (key === "birthPlace") return 0.9;
    if (key === "uniqueIden") return 1.8;
    if (["1", "2", "3", "4", "5", "6"].includes(key)) return 0.3;
    if (["trim1", "trim2", "trim3"].includes(key)) return 1.2;
    if (key === "decision") return 2.2;
    if (key === "moy") return 1;
    if (key === "handicape") return 0.8;
    return 1;
  };
  const weights = expandedColumns.map(weightFor);
  const sum = weights.reduce((a, b) => a + b, 0);
  expandedColumns.forEach((key, i) => {
    const style = { cellWidth: (weights[i] / sum) * tableWidth };
    const fixedFontSize = resolveColumnFixedFontSize(fontSizes, key);
    if (fixedFontSize != null) {
      // An explicit per-column size overrides every automatic rule below.
      style.fontSize = fixedFontSize;
      style.overflow = "visible";
    } else {
      if (["trim1", "trim2", "trim3", "moy"].includes(key)) {
        style.fontSize = PV_TABLE_HEAD_FONT;
      }
      // Birth date and Decision get a bigger font and never wrap mid-character.
      if (key === "decision" || key === "birthDate") {
        style.fontSize = PV_TABLE_WIDE_FONT;
        style.overflow = "visible";
      }
    }
    styles[i] = style;
  });
  return styles;
}

function createPvTableDidParseCell(doc, expandedColumns = [], fontSizes) {
  return (data) => {
    if (data.section === "head") {
      data.cell.styles.fontSize = PV_TABLE_HEAD_FONT;
      data.cell.styles.cellPadding = PV_TABLE_CELL_PADDING;
      data.cell.styles.valign = "middle";
      data.cell.styles.overflow = "visible";
      if (data.cell.text?.length) {
        const text = data.cell.text.join("\n").replace(/\u00A0/g, " ");
        const padding =
          (typeof data.cell.styles.cellPadding === "number"
            ? data.cell.styles.cellPadding
            : PV_TABLE_CELL_PADDING) * 2;
        const maxWidth = data.cell.width - padding;
        data.cell.text = text
          .split("\n")
          .flatMap((line) =>
            splitLabelAtWordBoundaries(
              doc,
              line.trim(),
              maxWidth,
              PV_TABLE_HEAD_FONT
            )
          );
      }
      return;
    }
    if (data.section === "body" && typeof data.cell.raw === "string") {
      const raw = data.cell.raw;
      const columnKey = expandedColumns[data.column.index];
      const fixedFontSize = resolveColumnFixedFontSize(fontSizes, columnKey);
      if (fixedFontSize != null) {
        // Honor the explicit size while keeping the same text normalization the
        // automatic branches apply (date hyphens, Arabic spacing, word wrap).
        if (/^\d{4}[-\u2011]\d{2}[-\u2011]\d{2}$/.test(raw)) {
          data.cell.text = [raw.replace(/-/g, "\u2011")];
        } else if (/[\u0600-\u06ff]/.test(raw)) {
          const normalized = formatTableCellText(raw).replace(/\u00a0/g, " ");
          const padding =
            (typeof data.cell.styles.cellPadding === "number"
              ? data.cell.styles.cellPadding
              : PV_TABLE_CELL_PADDING) * 2;
          const maxWidth = data.cell.width - padding;
          data.cell.text = splitLabelAtWordBoundaries(
            doc,
            normalized,
            maxWidth,
            fixedFontSize
          );
        } else {
          const formatted = formatTableCellText(raw);
          if (formatted !== raw) data.cell.text = [formatted];
        }
        data.cell.styles.fontSize = fixedFontSize;
        data.cell.styles.overflow = "visible";
        return;
      }
      if (/^\d{4}[-\u2011]\d{2}[-\u2011]\d{2}$/.test(raw)) {
        data.cell.text = [raw.replace(/-/g, "\u2011")];
        data.cell.styles.fontSize = PV_TABLE_WIDE_FONT;
        data.cell.styles.overflow = "visible";
        return;
      }
      if (
        columnKey === "decision" &&
        /[\u0600-\u06FF]/.test(raw)
      ) {
        const normalized = formatTableCellText(raw).replace(/\u00A0/g, " ");
        const padding =
          (typeof data.cell.styles.cellPadding === "number"
            ? data.cell.styles.cellPadding
            : PV_TABLE_CELL_PADDING) * 2;
        const maxWidth = data.cell.width - padding;
        data.cell.text = splitLabelAtWordBoundaries(
          doc,
          normalized,
          maxWidth,
          PV_TABLE_WIDE_FONT
        );
        data.cell.styles.fontSize = PV_TABLE_WIDE_FONT;
        data.cell.styles.overflow = "visible";
        return;
      }
      const formatted = formatTableCellText(raw);
      if (formatted !== raw) {
        data.cell.text = [formatted];
      }
      data.cell.styles.fontSize = PV_TABLE_BODY_FONT;
    }
  };
}

function getPvTableAutoTableOptions(doc, extra = {}) {
  const { expandedColumns, fontSizes, ...rest } = extra;
  return {
    margin: { left: 5, right: 5, bottom: 4 },
    pageBreak: "auto",
    rowPageBreak: "auto",
    styles: {
      font: "Amiri",
      fontSize: PV_TABLE_BODY_FONT,
      halign: "center",
      textColor: [0, 0, 0],
      textDirection: "rtl",
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      cellPadding: PV_TABLE_CELL_PADDING,
      overflow: "linebreak",
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "center",
      fontSize: PV_TABLE_HEAD_FONT,
      cellPadding: PV_TABLE_CELL_PADDING,
      valign: "middle",
      overflow: "visible",
    },
    didParseCell: createPvTableDidParseCell(doc, expandedColumns, fontSizes),
    ...rest,
  };
}

function isEmptyRichText(html) {
  if (!html) return true;
  const text = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return text.length === 0;
}

function normalizeRichTextHtml(html) {
  if (!html) return "";
  return html
    .replace(/(<p><br><\/p>\s*)+$/i, "")
    .replace(/(<p>\s*<\/p>\s*)+$/i, "")
    .trim();
}

function decodeRichTextEntities(text) {
  return String(text)
    .replace(/&nbsp;/gi, "\u00A0")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([\da-fA-F]+);/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
}

// Arabic names per class level. Mirrors getLevelNameArabic in the bulletin
// helpers (studentMarks/scripts/bulletinHeplers/pv.js); kept local here to keep
// this PDF module self-contained.
const LEVEL_NAME_BY_NUMBER = {
  "-2": "مستوى ما قبل التحضيري",
  "-1": "مستوى تحضيري",
  0: "مستوى تحضيري",
  1: "السنة الأولى",
  2: "السنة الثانية",
  3: "السنة الثالثة",
  4: "السنة الرابعة",
  5: "السنة الخامسة",
  6: "السنة السادسة",
  7: "السنة السابعة",
  8: "السنة الثامنة",
  9: "السنة التاسعة",
  10: "السنة الأولى ثانوي",
  11: "السنة الثانية ثانوي",
  12: "السنة الثالثة ثانوي",
  13: "السنة الرابعة ثانوي",
};

function getLevelNameArabic(level) {
  return LEVEL_NAME_BY_NUMBER[level] ?? `السنة ${level}`;
}

/**
 * Replace [[+0]] / [[+n]] / [[-n]] placeholders in a rich-text block with the
 * Arabic name of (class level + offset). [[+0]] is the current level, [[+1]]
 * the next, [[-1]] the previous, etc. This lets one template reference the
 * current level and its neighbours, e.g. a passing rule
 * "يرتقي من [[+0]] إلى [[+1]]". The "LEVEL" keyword was dropped so the token
 * stays language-neutral in the settings editor. When the level is unknown the
 * original text is returned untouched so the placeholder never renders as
 * garbage.
 */
const LEVEL_PLACEHOLDER_RE = /\[\[([+-]\d+)\]\]/g;
function substituteLevelPlaceholders(html, level) {
  if (!html) return html;
  const base = Number(level);
  if (!Number.isFinite(base)) return html;
  return html.replace(LEVEL_PLACEHOLDER_RE, (_match, offset) =>
    getLevelNameArabic(base + Number(offset))
  );
}

/**
 * Prepare Quill HTML for rasterization.
 *
 * We deliberately do NOT manually re-order or wrap number/Latin runs: the
 * browser's native Unicode Bidirectional Algorithm already lays out mixed
 * Arabic + digits (e.g. "17 أكتوبر 2024", "126-10-2024") correctly when the
 * paragraph has `unicode-bidi: plaintext`. Manual wrapping previously
 * corrupted words such as "أكتوبر" and turned `&nbsp;` into literal text, so
 * here we only decode HTML entities inside text nodes.
 */
function prepareRichTextHtmlForPdf(html, level) {
  const normalized = normalizeRichTextHtml(substituteLevelPlaceholders(html, level));
  if (!normalized) return "";
  return normalized.replace(/>([^<]+)</g, (match, text) => {
    const decoded = decodeRichTextEntities(text);
    return decoded === text ? match : `>${decoded}<`;
  });
}

const RICH_TEXT_FONT_FACE_ID = "pv-rich-text-amiri-face";
let richTextFontsPromise = null;

async function ensureRichTextFonts() {
  if (!richTextFontsPromise) {
    richTextFontsPromise = (async () => {
      if (!document.getElementById(RICH_TEXT_FONT_FACE_ID)) {
        const [{ default: amiriNormal }, { default: amiriBold }] =
          await Promise.all([
            import("raw-loader!@/assets/fonts/Amiri.txt"),
            import("raw-loader!@/assets/fonts/Amiri-Bold.txt"),
          ]);
        const style = document.createElement("style");
        style.id = RICH_TEXT_FONT_FACE_ID;
        style.textContent = `
          @font-face {
            font-family: "Amiri";
            src: url("data:font/truetype;charset=utf-8;base64,${amiriNormal.trim()}") format("truetype");
            font-weight: 400;
            font-style: normal;
          }
          @font-face {
            font-family: "Amiri";
            src: url("data:font/truetype;charset=utf-8;base64,${amiriBold.trim()}") format("truetype");
            font-weight: 700;
            font-style: normal;
          }
        `;
        document.head.appendChild(style);
      }
      if (document.fonts?.load) {
        await Promise.all([
          document.fonts.load('400 14px "Amiri"'),
          document.fonts.load('700 14px "Amiri"'),
        ]).catch(() => { });
        await document.fonts.ready;
      }
    })();
  }
  return richTextFontsPromise;
}

function measureRichTextHeight(container) {
  const baseTop = container.getBoundingClientRect().top;
  let maxBottom = 0;
  container.querySelectorAll("p, li, ol, ul, h1, h2, h3, blockquote").forEach(
    (node) => {
      const rect = node.getBoundingClientRect();
      if (rect.height > 0) {
        maxBottom = Math.max(maxBottom, rect.bottom - baseTop);
      }
    }
  );
  if (maxBottom > 0) return Math.ceil(maxBottom);
  return Math.ceil(Math.max(container.offsetHeight, container.scrollHeight, 1));
}

/**
 * Cache of rasterized rich-text blocks keyed by normalized HTML.
 * Styling (CSS/scale/width) is constant, so the HTML fully determines the
 * output. Values are Promises so concurrent identical requests share one
 * render (in-flight dedup). Read-only reuse is safe: callers only draw FROM
 * the cached canvas, never mutate it.
 */
const RICH_TEXT_CACHE = new Map();
const RICH_TEXT_CACHE_MAX = 60;

let fontsReadyPromise = null;
function whenFontsReady() {
  if (!document.fonts?.ready) return Promise.resolve();
  if (!fontsReadyPromise) fontsReadyPromise = document.fonts.ready;
  return fontsReadyPromise;
}

const RICH_TEXT_STYLE = `
    .ql-editor, .ql-editor p, .ql-editor ol, .ql-editor ul, .ql-editor li, .ql-editor span {
      margin: 0 !important;
      padding: 0 !important;
      color: #000000 !important;
      font-family: "Amiri", "Noto Naskh Arabic", serif !important;
      letter-spacing: 0 !important;
      word-spacing: normal !important;
      font-kerning: normal !important;
    }
    .ql-editor {
      min-height: 0 !important;
      height: auto !important;
      overflow: visible !important;
      color: #000000 !important;
    }
    .ql-container {
      height: auto !important;
      min-height: 0 !important;
    }
    /*
     * unicode-bidi: plaintext lets the browser pick each paragraph's base
     * direction from its first strong character (Arabic => RTL) and lay out
     * embedded digits/Latin runs with the native bidi algorithm. This renders
     * "17 أكتوبر 2024" and "126-10-2024" correctly without manual wrapping.
     */
    .ql-editor p, .ql-editor li, .ql-direction-rtl {
      direction: rtl;
      text-align: right;
      unicode-bidi: plaintext;
    }
    .ql-editor p + p { margin-top: 4px !important; }
    .ql-editor li + li { margin-top: 3px !important; }
  `;

async function rasterizeRichText(normalized) {
  await ensureRichTextFonts();
  const container = document.createElement("div");
  container.className = "ql-editor";
  container.style.direction = "rtl";
  container.style.textAlign = "right";
  container.style.fontFamily = '"Amiri", "Noto Naskh Arabic", serif';
  container.style.letterSpacing = "0";
  container.style.wordSpacing = "normal";
  container.style.backgroundColor = "white";
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.style.width = "680px";
  container.style.padding = "0";
  container.style.margin = "0";
  container.style.lineHeight = "1.5";
  container.style.fontSize = "14px";
  container.style.overflow = "visible";
  container.innerHTML = normalized;

  const styleTag = document.createElement("style");
  styleTag.textContent = quillSnowCss + RICH_TEXT_STYLE;
  document.head.appendChild(styleTag);
  document.body.appendChild(container);

  try {
    await whenFontsReady();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const captureHeight = measureRichTextHeight(container);
    container.style.height = `${captureHeight}px`;

    const canvas = await html2canvas(container, {
      backgroundColor: "#ffffff",
      scale: 2,
      logging: false,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      width: container.scrollWidth,
      height: captureHeight,
      windowWidth: container.scrollWidth,
      windowHeight: captureHeight,
    });

    return trimCanvasWhitespace(canvas);
  } finally {
    if (container.parentNode) document.body.removeChild(container);
    if (styleTag.parentNode) document.head.removeChild(styleTag);
  }
}

async function htmlStringToImage(htmlString, level) {
  const normalized = prepareRichTextHtmlForPdf(htmlString, level);
  if (!normalized) return null;

  if (RICH_TEXT_CACHE.has(normalized)) {
    return RICH_TEXT_CACHE.get(normalized);
  }

  const pending = rasterizeRichText(normalized);
  RICH_TEXT_CACHE.set(normalized, pending);

  try {
    const result = await pending;
    // Refresh recency and cap cache size (simple LRU).
    RICH_TEXT_CACHE.delete(normalized);
    RICH_TEXT_CACHE.set(normalized, Promise.resolve(result));
    if (RICH_TEXT_CACHE.size > RICH_TEXT_CACHE_MAX) {
      const oldest = RICH_TEXT_CACHE.keys().next().value;
      RICH_TEXT_CACHE.delete(oldest);
    }
    return result;
  } catch (error) {
    RICH_TEXT_CACHE.delete(normalized);
    throw error;
  }
}

/** Rasterize every rich-text block this config will draw, in parallel, to warm the cache. */
async function prewarmRichText(config, level) {
  const texts = [
    config.table?.preParagraph?.allow && config.table.preParagraph.richText,
    config.table?.postParagraph?.allow && config.table.postParagraph.richText,
    config.footer?.exceptionalPass?.allow &&
    config.footer.exceptionalPass.richText,
    config.footer?.agePass?.allow && config.footer.agePass.richText,
    config.footer?.fail?.allow && config.footer.fail.richText,
  ].filter((t) => t && !isEmptyRichText(t));

  await Promise.all(
    texts.map((t) => htmlStringToImage(t, level).catch(() => null))
  );
}

function trimCanvasWhitespace(canvas, whiteThreshold = 248) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  if (!width || !height) {
    return { canvas, width: canvas.width, height: canvas.height };
  }

  const { data } = ctx.getImageData(0, 0, width, height);
  const rowHasInk = (y) => {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const a = data[i + 3];
      if (a < 8) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (r < whiteThreshold || g < whiteThreshold || b < whiteThreshold) {
        return true;
      }
    }
    return false;
  };

  let top = 0;
  while (top < height - 1 && !rowHasInk(top)) top += 1;

  let bottom = height - 1;
  while (bottom > top && !rowHasInk(bottom)) bottom -= 1;

  const trimmedHeight = bottom - top + 1;
  if (trimmedHeight <= 0 || trimmedHeight >= height) {
    return { canvas, width: canvas.width, height: canvas.height };
  }

  const trimmed = document.createElement("canvas");
  trimmed.width = width;
  trimmed.height = trimmedHeight;
  trimmed
    .getContext("2d")
    .drawImage(canvas, 0, top, width, trimmedHeight, 0, 0, width, trimmedHeight);

  return { canvas: trimmed, width: trimmed.width, height: trimmed.height };
}

function cropCanvasSlice(canvas, srcY, srcH) {
  const maxH = canvas.height - srcY;
  const h = Math.max(1, Math.min(Math.round(srcH), maxH));
  const slice = document.createElement("canvas");
  slice.width = canvas.width;
  slice.height = h;
  const ctx = slice.getContext("2d");
  ctx.drawImage(canvas, 0, srcY, canvas.width, h, 0, 0, canvas.width, h);
  return { slice, heightPx: h };
}

/**
 * Renders Quill HTML as an image, splitting across pages when it overflows.
 */
async function addRichTextImage(doc, richText, StartY, useRect, level) {
  if (isEmptyRichText(richText)) return StartY;

  const pdf_width = doc.internal.pageSize.width;
  const pdf_height = doc.internal.pageSize.height;
  const bottomMargin = 6;
  const topMargin = 6;
  const leftMargin = 10;
  const pdfW = pdf_width - 20;
  const sectionGap = 1;

  const rendered = await htmlStringToImage(richText, level);
  if (!rendered?.canvas || rendered.height <= 0) return StartY;

  const { canvas, width: pxWidth, height: pxHeight } = rendered;
  const pxToPdf = pdfW / pxWidth;

  let y = StartY;
  let srcY = 0;

  while (srcY < pxHeight) {
    let availablePdfH = pdf_height - y - bottomMargin;
    if (availablePdfH < 8) {
      doc.addPage();
      y = topMargin;
      availablePdfH = pdf_height - y - bottomMargin;
    }

    const remainingPx = pxHeight - srcY;
    const slicePxH = Math.floor(
      Math.min(remainingPx, (availablePdfH - 1) / pxToPdf)
    );
    if (slicePxH < 1) {
      doc.addPage();
      y = topMargin;
      continue;
    }

    const { slice, heightPx } = cropCanvasSlice(canvas, srcY, slicePxH);
    const slicePdfH = heightPx * pxToPdf;

    try {
      doc.addImage(
        slice.toDataURL("image/png"),
        "PNG",
        leftMargin,
        y,
        pdfW,
        slicePdfH
      );
    } catch (error) {
      console.info("Cannot draw paragraph richTextImage", error);
      break;
    }

    srcY += heightPx;
    y += slicePdfH + (srcY < pxHeight ? 0 : sectionGap);

    if (srcY < pxHeight) {
      doc.addPage();
      y = topMargin;
    }
  }

  return y;
}

function startRichTextBlock(doc, startY, minFirstSlice = 8) {
  const pdf_height = doc.internal.pageSize.height;
  if (startY + minFirstSlice > pdf_height - 6) {
    doc.addPage();
    return 6;
  }
  return startY;
}

function sectionStartY(doc, lastY, gap = 2) {
  const pdf_height = doc.internal.pageSize.height;
  const nextY = lastY + gap;
  if (nextY > pdf_height - 8) {
    doc.addPage();
    return 6;
  }
  return nextY;
}

function pdfImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return "JPEG";
  if (dataUrl.includes("image/png")) return "PNG";
  if (dataUrl.includes("image/webp")) return "WEBP";
  return "JPEG";
}

// Strongly right-to-left characters (Arabic, incl. presentation forms).
const ARABIC_CHAR_RE =
  /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
// Strongly left-to-right characters: Latin letters and digits.
const LTR_CHAR_RE = /[A-Za-z0-9À-ɏ٠-٩]/;

function classifyBidiChar(ch) {
  if (ARABIC_CHAR_RE.test(ch)) return "ar";
  if (LTR_CHAR_RE.test(ch)) return "ltr";
  return "neutral";
}

/**
 * Splits a mixed Arabic/Latin string into directional units in LOGICAL order.
 *
 * jsPDF shapes Arabic then runs its bidi engine, which scrambles mixed runs
 * (e.g. it splits "6éme A" because the digit binds to the adjacent Arabic).
 * Instead we draw each unit ourselves: Latin/digit cores stay intact as one
 * LTR unit, Arabic letters form RTL units, and neutral characters (spaces,
 * ":", ...) become their own units so they land next to the right neighbour
 * after the run order is reversed for the RTL line. Neutrals already inside an
 * LTR core (the space in "6éme A") stay attached to it.
 */
function splitBidiUnits(text) {
  const units = [];
  let buf = "";
  let type = null;
  const flush = () => {
    if (buf) units.push({ text: buf, ltr: type === "ltr" });
    buf = "";
    type = null;
  };
  for (const ch of String(text)) {
    const t = classifyBidiChar(ch);
    if (t === "neutral") {
      if (type === "ltr") {
        buf += ch;
        continue;
      }
      flush();
      units.push({ text: ch, ltr: false });
      continue;
    }
    if (type === null || t === type) {
      buf += ch;
      type = t;
      continue;
    }
    flush();
    buf = ch;
    type = t;
  }
  flush();
  return units;
}

/**
 * Draws a mixed Arabic/Latin line centered on `centerX`, laying the logical
 * units out right-to-left (RTL base direction) so the Latin segments keep their
 * order and the Arabic segments are shaped correctly by the normal text path.
 */
function drawRtlMixedLine(doc, text, centerX, y) {
  const units = splitBidiUnits(text);
  const widths = units.map((u) => doc.getTextWidth(u.text));
  const total = widths.reduce((a, b) => a + b, 0);
  let x = centerX - total / 2;
  // Visual left-to-right order for an RTL line is the reverse of logical order.
  for (let i = units.length - 1; i >= 0; i--) {
    doc.text(units[i].text, x, y);
    x += widths[i];
  }
  return total;
}

function heterogeneousTexts(doc, leftText, rightText, startY, startX, padding = 10, spacement = 2, centring = false) {
  if (centring) {
    const leftWidth = doc.getTextWidth(leftText);
    const rightWidth = doc.getTextWidth(rightText);
    const leftX = startX - (leftWidth + rightWidth + spacement) / 2
    const rightX = leftX + leftWidth + spacement

    doc.text(rightText, rightX, startY);
    doc.text(leftText, leftX, startY);

    return doc
  } else {
    const rightWidth = doc.getTextWidth(rightText);
    const rightX = startX - rightWidth - padding
    doc.text(rightText, rightX, startY);

    const leftWidth = doc.getTextWidth(leftText);
    const leftX = startX - rightWidth - leftWidth - padding - spacement
    doc.text(leftText, leftX, startY);

    return doc
  }

}

async function drawHeaders(doc, config, data, info, pageNumber = 1) {
  const TOPY = 10;
  const pdf_width = doc.internal.pageSize.width;
  const pdf_height = doc.internal.pageSize.height;
  doc.setFont("Amiri", "normal");
  let yCenter = TOPY,
    yLeft = TOPY,
    yRight = TOPY,
    text,
    width,
    x;
  const leftStart = pdf_width - pdf_width * 0.05;
  const SCHOOL_LOGO_HEIGHT = 10;
  const SCHOOL_LOGO_GAP = 5;
  function getStatsTableStartY() {
    if (config.headers?.schoolLogo?.allow && info.schoolLogo) {
      const SCALE = config.headers?.schoolLogo?.scale || 1
      console.info({
        TOPY,
        SCHOOL_LOGO_HEIGHT,
        SCALE,
        SCHOOL_LOGO_GAP
      })
      return TOPY + SCHOOL_LOGO_HEIGHT * SCALE + SCHOOL_LOGO_GAP;
    }
    if (config.headers?.govLog?.allow) {
      return 20 * config.headers?.govLog?.scale
    }
    return TOPY;
  }
  let center;
  function lastY() {
    return Math.max(yCenter, yLeft, yRight);
  }
  // ################# Header Sections ######################
  // ######## Left Headers ############
  // gov text
  if (config.headers?.govText?.allow) {
    doc.setFont("Amiri", "normal");
    doc.setFontSize(12);
    //doc.text("الجمهورية التونسية", leftStart, yCenter, { align: "center" });
    //yCenter += 7;
    //doc.text("وزارة التربية", leftStart, yCenter, { align: "center" });
    //yCenter += 7;
    const govTexts = []
    if (config.headers?.govText?.useRegionalText)
      govTexts.push(doc.getTextWidth("المندوبيّة الجهويّة للتربيّة ب" + info.region))

    govTexts.push(doc.getTextWidth(" المدرسة الابتدائية" + info.schoolName),)

    if (config.headers?.govText?.useSchoolCode)
      govTexts.push(doc.getTextWidth(" :رمزها" + info.schoolCode || " "));

    let classroomName;
    if (config.headers?.govText?.useClassName) {
      // find classRoomName if defined
      classroomName = (data.students || []).find(s => s.classroomName)?.classroomName

      if (classroomName)
        govTexts.push(doc.getTextWidth("القسم: " + classroomName));

    }
    center = leftStart - Math.max(...govTexts) / 2;

    if (config.headers?.govText?.useSchoolCode) {
      doc = heterogeneousTexts(
        doc,
        info.region,
        "المندوبيّة الجهويّة للتربيّة ب",
        yLeft,
        center,
        0,
        0,
        true
      )
      yLeft += 7;
    }

    doc = heterogeneousTexts(
      doc,
      info.schoolName,
      "المدرسة الابتدائية",
      yLeft,
      center,
      0,
      2,
      true
    )
    yLeft += 7;

    if (config.headers?.govText?.useSchoolCode) {
      doc = heterogeneousTexts(
        doc,
        info.schoolCode,
        ":رمزها ",
        yLeft,
        center,
        0,
        2,
        true
      )
      yLeft += 7;
    }
    if (config.headers?.govText?.useClassName && classroomName) {
      doc = heterogeneousTexts(
        doc,
        classroomName,
        ":القسم ",
        yLeft,
        center,
        0,
        2,
        true
      )
      yLeft += 7;
    }
  }
  // schoolar year
  if (config.headers?.schoolYear?.allow) {
    if (config.headers?.govText?.allow) yLeft += 5;
    let rectStartY = yLeft,
      rectwrapWidth = 0,
      textWraptStart = pdf_width;
    if (config.headers?.schoolYear?.rect) yLeft += 7;
    else yLeft += 3;
    doc.setFontSize(14);

    text = ":السنة الدّراسيّة";
    width = doc.getTextWidth(text);
    rectwrapWidth = width > rectwrapWidth ? width : rectwrapWidth;
    x = center ? center - width / 2 : leftStart - width;
    textWraptStart = textWraptStart < x ? textWraptStart : x;
    doc.text(text, x, yLeft);

    yLeft += 7;
    text = info.schoolYear;
    width = doc.getTextWidth(text);
    yCenter = yLeft - 3.5;
    rectwrapWidth = width > rectwrapWidth ? width : rectwrapWidth;
    x = center ? center - width / 2 : leftStart - width;
    textWraptStart = textWraptStart < x ? textWraptStart : x;
    doc.text(text, x, yLeft);

    if (config.headers?.schoolYear?.rect) {
      //doc.rect(x,y,w,h,)
      yLeft = rectStartY + yLeft - rectStartY + 3;
      doc.rect(
        textWraptStart - 10,
        rectStartY,
        rectwrapWidth + 20,
        yLeft - rectStartY + 3
      );
    }
  }
  // ######## center Headers ############
  if (config.headers?.govLog?.allow) {
    try {
      const scale = config.headers?.govLog?.scale || 1
      doc.addImage(
        "/resources/images/govImg.png",
        "JPEG",
        pdf_width / 2 - 12.5,
        TOPY,
        25 * scale,
        25 * scale
      );
      yCenter = TOPY + 32;
    } catch (error) {
      console.info("cannot draw gov log");
    }
  }
  if (config.headers?.mainTitle?.allow) {
    const defaultMain = {
      allow: true,
      repeatEachPage: false,
      font: "Amiri",
      fontStyle: "Bold",
      fontSize: 14,
      color: "#000000",
      text: "قسم جلسة محضر",
    };
    doc.setFont(
      config.headers?.mainTitle?.font || defaultMain.font,
      config.headers?.mainTitle?.fontStyle || defaultMain.fontStyle
    );
    doc.setFontSize(
      config.headers?.mainTitle?.fontSize || defaultMain.fontSize
    );
    // Force pure black for maximum legibility (overrides legacy greyish configs).
    doc.setTextColor(0, 0, 0);
    text = config.headers?.mainTitle?.text || defaultMain.text;
    width = doc.getTextWidth(text);
    doc.text(text, pdf_width / 2 - width / 2, yCenter);
    yCenter += 8;
  }
  if (config.headers?.secondaryText?.allow) {
    const defaultMain = {
      allow: true,
      repeatEachPage: false,
      font: "Amiri",
      fontStyle: "Bold",
      fontSize: 14,
      color: "#000000",
      text: " ",
    };
    doc.setFont(
      config.headers?.secondaryText?.font || defaultMain.font,
      config.headers?.secondaryText?.fontStyle || defaultMain.fontStyle
    );
    doc.setFontSize(
      config.headers?.secondaryText?.fontSize || defaultMain.fontSize
    );
    // Force pure black for maximum legibility (overrides legacy greyish configs).
    doc.setTextColor(0, 0, 0);
    text = config.headers?.secondaryText?.text || defaultMain.text;
    width = doc.getTextWidth(text);
    doc.text(text, pdf_width / 2 - width / 2, yCenter);
  }
  // ######## left Headers ############
  doc.setFont("Amiri", "normal");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  if (config.headers?.schoolLogo?.allow && info.schoolLogo) {
    try {
      const scale = config.headers?.schoolLogo?.scale
      doc.addImage(
        info.schoolLogo,
        pdfImageFormat(info.schoolLogo),
        10,
        TOPY,
        10 * scale,
        10 * scale
      );
    } catch (error) {
      console.info("Cannot draw school logo", error);
    }
  }
  if (config.headers?.stats?.allow) {
    const nbT = data.nbInscrit?.T || "--";
    const nbM = data.nbInscrit?.M;
    const nbF = data.nbInscrit?.F || "--";
    const nbAOnly = (data.nbA?.M ?? 0) + (data.nbA?.F ?? 0);
    const nbCOnly = (data.nbC?.M ?? 0) + (data.nbC?.F ?? 0);
    const nbPOnly = (data.nbP?.M ?? 0) + (data.nbP?.F ?? 0);
    const nbA = nbAOnly + nbCOnly + nbPOnly || "--";
    const nbAM =
      (data.nbA?.M ?? 0) + (data.nbC?.M ?? 0) + (data.nbP?.M ?? 0) || "--";
    const nbAF =
      (data.nbA?.F ?? 0) + (data.nbC?.F ?? 0) + (data.nbP?.F ?? 0) || "--";
    if (config.headers?.stats?.rect) {
      // Table header row with colspan
      const head = [
        [
          {
            content: "معطيات إحصائية",
            colSpan: 4,
            styles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
          },
        ],
      ];

      // Body rows
      const body = [
        [nbT, { content: "العدد الجملي للتلاميذ", colSpan: 3 }],
        [nbM, "ذكور", nbF, "إناث"],
        [nbA, { content: "العدد الجملي للمرتقين", colSpan: 3 }],
        [nbAM, "ذكور", nbAF, "إناث"],
      ];

      doc.autoTable({
        head: head,
        body: body,
        startY: getStatsTableStartY(),
        margin: { left: 10 },
        tableWidth: config.orientation ? pdf_width / 3.5 : pdf_width / 4,
        styles: {
          lineWidth: 0.1, // or higher for thicker borders
          lineColor: [0, 0, 0], // black borders
          halign: "center",
          font: "Amiri", // If using Arabic font, replace with your custom font
          fontStyle: "normal",
          textDirection: "rtl",
          fontSize: 10,
        },
        didParseCell: function (data) {
          // Gray background for cells containing "ذكور" and "إناث"
          if (data.cell.raw === "ذكور" || data.cell.raw === "إناث") {
            data.cell.styles.fillColor = [200, 200, 200];
          } else if (typeof data.cell.raw == "number") {
            data.cell.styles.fillColor = [255, 255, 255];
          }
        },
      });
      yRight = doc.lastAutoTable.finalY;
    } else {
      let startY = getStatsTableStartY(),
        startX = 10,
        lineSpacing = 7;
      doc.text("معطيات إحصائية", startX + 10, startY);

      startY += lineSpacing;

      // Row 1
      doc.text(`${nbT}    العدد الجملي للتلاميذ`, startX, startY);
      startY += lineSpacing;

      // Row 2
      doc.text(`${nbM}    ذكور     ${nbF}    إناث`, startX, startY);
      startY += lineSpacing;

      // Row 3
      doc.text(`${nbA}    العدد الجملي للمرتقين`, startX, startY);
      startY += lineSpacing;

      // Row 4
      doc.text(`${nbAM}    ذكور     ${nbAF}    إناث`, startX, startY);
      yRight = startY;
    }
  }
  // ######## Table pre paragraph ############
  if (config.table?.preParagraph.allow) {
    yLeft = await addRichTextImage(
      doc,
      config.table.preParagraph.richText,
      startRichTextBlock(doc, lastY() + 2),
      config.table?.preParagraph?.rect,
      data.level
    );
  }
  return {
    doc,
    yLeft,
    yRight,
    yCenter,
    lastY: Math.max(yLeft, yRight, yCenter),
  };
}
async function drawTableAdmin(doc, data, config, startY, levelData = []) {
  const pdf_width = doc.internal.pageSize.width;
  const tableConfig = cloneDeep(config.table);
  // pre process
  const prevYears = tableConfig.prevYears === true;
  // fullName is mutually exclusive with firstName/lastName.
  if (tableConfig.fullName) {
    tableConfig.firstName = false;
    tableConfig.lastName = false;
  }
  // creating lables
  const labels = {
    handicape: formatTableLabel("التلامذة ذوي الإعاقة"),
    decision: formatTableLabel("قرار\nالمجلس"),
    moy: formatTableLabel("المعدل\nالسنوي"),
    trim3: formatTableLabel("الثلاثي\nالثالث"),
    trim2: formatTableLabel("الثلاثي\nالثاني"),
    trim1: formatTableLabel("الثلاثي\nالأول"),
    prevYears: formatTableLabel("مدة الدراسة"),
    uniqueIden: formatTableLabel("المعرف الوحيد"),
    birthPlace: formatTableLabel("مكان\nالولادة"),
    birthDate: formatTableLabel("تاريخ\nالولادة"),
    gender: formatTableLabel('الجنس'),
    lastName: formatTableLabel("اللقب"),
    firstName: formatTableLabel("الاسم"),
    fullName: formatTableLabel("الاسم واللقب"),
    number: formatTableLabel("ع/ر"),
  };

  // Honor the saved ordering, then append any enabled canonical column that the
  // persisted order is missing (older templates lack e.g. "gender"). Dedupe so a
  // corrupted order with a column twice can't render duplicate columns.
  const savedOrder = tableConfig.columnsOrder || [];
  const seen = new Set();
  const orderedColumns = [...savedOrder, ...ADMIN_TABLE_COLUMN_ORDER].filter(
    (key) => {
      if (!ADMIN_TABLE_COLUMN_ORDER.includes(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    }
  );
  const activeColumns = orderedColumns
    .filter((key) => tableConfig[key])
    .reverse();

  const expandedColumns = [];
  activeColumns.forEach((col) => {
    if (col === "prevYears" && prevYears) {
      expandedColumns.push("6", "5", "4", "3", "2", "1");
    } else {
      expandedColumns.push(col);
    }
  });

  // First row
  const headRows = [];

  const firstRow = activeColumns.map((col) => {
    if (prevYears) {
      if (col === "prevYears") {
        return {
          content: labels[col],
          colSpan: 6,
          styles: { halign: "center" },
        };
      } else
        return {
          content: labels[col],
          rowSpan: 2,
          styles: { halign: "center" },
        };
    }

    return labels[col];
  });
  headRows.push(firstRow);
  // Second row: only the prevYears sub-headers. Every other column uses
  // rowSpan: 2 in the first row, and autotable auto-skips those slots here — so
  // emitting placeholder cells for them would consume the prevYears slots and
  // push the real digits out (only "6" survived). The digits run 6→1 left to
  // right so they read 1→6 right-to-left in the RTL table.
  const secondRow = activeColumns.flatMap((col) => {
    if (col === "prevYears" && prevYears) {
      return [
        { content: "6", colSpan: 1, rowSpan: 1 },
        { content: "5", colSpan: 1, rowSpan: 1 },
        { content: "4", colSpan: 1, rowSpan: 1 },
        { content: "3", colSpan: 1, rowSpan: 1 },
        { content: "2", colSpan: 1, rowSpan: 1 },
        { content: "1", colSpan: 1, rowSpan: 1 },
      ];
    }
    return [];
  });
  headRows.push(secondRow);
  // rows data
  const adminStudent = data.students?.filter(
    (s) => s.decisionConseilKey == "A"
  );
  const length = adminStudent?.length || 0;
  const rows = Array.from({ length }, (_, i) => {
    const currentStudent = adminStudent[i]
    const studentYearsLevel = findYearsPerLevel(
      currentStudent.id ?? currentStudent._id,
      levelData,
      data.level
    )
    return prevYears
      ? {
        handicape: currentStudent.handicape || "",
        decision: currentStudent.decisionConseil || "",
        moy: currentStudent.finalMoyenne || "",
        trim3: currentStudent.moyenneThree || "",
        trim2: currentStudent.moyenneTwo || "",
        trim1: currentStudent.moyenneOne || "",
        1: studentYearsLevel[1],
        2: studentYearsLevel[2],
        3: studentYearsLevel[3],
        4: studentYearsLevel[4],
        5: studentYearsLevel[5],
        6: studentYearsLevel[6],
        uniqueIden: currentStudent.uniqueId || "",
        birthPlace: currentStudent.birthPlace || "",
        birthDate: formatBirthDateYmd(currentStudent.BirthDate),
        gender: formatGender(currentStudent.gender),
        lastName: currentStudent.lastName || "",
        firstName: currentStudent.firstName || "",
        fullName: currentStudent.fullName || "",
        number: String(i + 1).padStart(2, "0"),
      }
      : {
        handicape: currentStudent.handicape || "",
        decision: currentStudent.decisionConseil || "",
        moy: currentStudent.moy || "",
        trim3: currentStudent.moyenneThree || "",
        trim2: currentStudent.moyenneTwo || "",
        trim1: currentStudent.moyenneOne || "",
        uniqueIden: currentStudent.uniqueId || "",
        birthPlace: currentStudent.birthPlace || "",
        birthDate: formatBirthDateYmd(currentStudent.BirthDate),
        gender: formatGender(currentStudent.gender),
        lastName: currentStudent.lastName || "",
        firstName: currentStudent.firstName || "",
        fullName: currentStudent.fullName || "",
        number: String(i + 1).padStart(2, "0"),
      }
  });

  const body = rows.map((row) =>
    expandedColumns.map((key) => formatTableCellText(row[key] || ""))
  );
  const tableWidth = pdf_width - 10;
  const fontSizes = tableConfig.fontSizes;
  doc.autoTable(
    getPvTableAutoTableOptions(doc, {
      expandedColumns,
      fontSizes,
      head: headRows.map((row) => row.map(formatHeadCell)),
      body,
      startY,
      showHead: "everyPage",
      columnStyles: buildMainTableColumnStyles(
        expandedColumns,
        tableWidth,
        fontSizes
      ),
    })
  );
  let lastY = doc.lastAutoTable.finalY;
  // ######## paragraph Header ############
  if (config.table?.postParagraph.allow) {
    lastY = await addRichTextImage(
      doc,
      config.table.postParagraph.richText,
      startRichTextBlock(doc, sectionStartY(doc, lastY)),
      config.table?.postParagraph?.rect,
      data.level
    );
  }
  return [doc, lastY];
}
// jsPDF's getTextWidth under-measures shaped Arabic (e.g. "اليوم" ~5mm), but
// autotable decides wrapping with splitTextToSize, which needs ~9mm. Binary
// search the smallest content width that keeps the text on a single line so
// fixed columns are sized to what autotable actually requires.
function singleLineContentWidth(doc, text) {
  let lo = 1;
  let hi = 200;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    if (doc.splitTextToSize(text, mid).length <= 1) hi = mid;
    else lo = mid;
  }
  return hi;
}

async function otherTables(doc, data, config, lastY, on) {
  const key =
    on == "exceptionalPass" ? "C" : on == "agePass" ? "P" : on == "fail" ? "R" : null;
  const students = key
    ? data.students?.filter((s) => s.decisionConseilKey == key)
    : [];
  const studentCount = students?.length || 0;
  const specCount = Math.max(0, Number(config.specCount) || 0);
  const filled = Boolean(config.filled);

  let rowCount;
  if (!filled) {
    rowCount = Math.max(specCount, 1);
  } else {
    rowCount = studentCount + specCount;
  }

  if (config.richText) {
    lastY = await addRichTextImage(
      doc,
      config.richText,
      startRichTextBlock(doc, sectionStartY(doc, lastY)),
      config.rect,
      data.level
    );
  }
  if (rowCount === 0) {
    return [doc, lastY];
  }
  // Define the two header rows
  let notes = "";
  if (on == "exceptionalPass") notes = "ملاحظات\n(المعدل الحسابي السنوي)";
  else if (on == "agePass")
    notes = `ملاحظات\n(تجاوز عمره 10 سنوات في 15 سبتمبر ${new Date().getFullYear()})`;
  else if (on == "fail") notes = "ملاحظات";

  const head = [
    // First header row with rowSpan / colSpan
    [
      { content: notes, rowSpan: 2 },
      { content: "المعدل السنوي", rowSpan: 2 },
      { content: "الجنس", rowSpan: 2 },
      { content: "تاريخ الولادة", colSpan: 3, styles: { halign: "center" } },
      { content: "الاسم واللقب", rowSpan: 2 },
      { content: "ع/ر", rowSpan: 2 },
    ],
    // Second header row: only the date sub-headers. Every other column uses
    // rowSpan: 2 above and autotable auto-skips those slots here, so emitting
    // empty placeholder cells for them would consume the date slots and push the
    // real sub-headers (السنة/الشهر/اليوم) out.
    ["السنة", "الشهر", "اليوم"],
  ];
  const body = Array.from({ length: rowCount }, (_, i) => {
    if (filled && i < studentCount) {
      const s = students[i];
      return [
        "",
        s.finalMoyenne,
        s?.gender == "male" ? "ذكر" : "أنثى",
        getMMDDYYY(s.BirthDate).yyyy,
        getMMDDYYY(s.BirthDate).mm,
        getMMDDYYY(s.BirthDate).dd,
        s.fullName,
        String(i + 1),
      ];
    }
    return ["", "", "", "", "", "", "", String(i + 1)];
  });
  // Fixed-content columns (year/month/day, gender, yearly average, order
  // number) are sized to just fit their header text so a long notes cell can't
  // squeeze them until the headers wrap mid-character. Notes and the full name
  // share the leftover width; the full name wraps on word boundaries.
  const pdf_width = doc.internal.pageSize.width;
  const tableWidth = pdf_width - 10; // matches left + right margins (5 + 5)
  doc.setFont("Amiri", "normal");
  doc.setFontSize(10);
  const sidePad = 1.5 * 2; // cellPadding on both sides
  // Single-word headers must never wrap mid-character, so size them by the width
  // autotable actually needs (splitTextToSize), not by getTextWidth.
  const oneLineW = (txt, buffer = 2) =>
    singleLineContentWidth(doc, txt) + sidePad + buffer;
  // "المعدل السنوي" is two words; keep it tight and let it wrap at the space.
  const moyW = doc.getTextWidth("المعدل السنوي") + sidePad + 4;
  const genderW = oneLineW("الجنس");
  const yearW = oneLineW("السنة");
  const monthW = oneLineW("الشهر");
  const dayW = oneLineW("اليوم");
  const numberW = oneLineW("ع/ر");
  const remaining = Math.max(
    0,
    tableWidth - (moyW + genderW + yearW + monthW + dayW + numberW)
  );
  const notesW = remaining * 0.45;
  const fullNameW = remaining - notesW;
  doc.autoTable({
    startY: lastY,
    head,
    body,
    margin: { left: 5, right: 5, bottom: 4 },
    pageBreak: "auto",
    rowPageBreak: "auto",
    tableWidth,
    columnStyles: {
      0: { cellWidth: notesW },
      1: { cellWidth: moyW },
      2: { cellWidth: genderW },
      3: { cellWidth: yearW },
      4: { cellWidth: monthW },
      5: { cellWidth: dayW },
      6: { cellWidth: fullNameW, overflow: "linebreak" },
      7: { cellWidth: numberW },
    },
    styles: {
      font: "Amiri",
      fontSize: 10,
      halign: "center",
      textDirection: "rtl",
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      cellPadding: 1.5,
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255], // No gray background
    },
    headStyles: {
      fillColor: [255, 255, 255], // white
      textColor: [0, 0, 0],
      halign: "center",
    },
  });
  lastY = doc.lastAutoTable.finalY;
  return [doc, lastY];
}
async function drawLastPage(doc, config, lastY) {
  const pdf_height = doc.internal.pageSize.getHeight();
  if (lastY > pdf_height - 28) {
    doc.addPage();
    lastY = 6;
  }
  const pdf_width = doc.internal.pageSize.getWidth();
  // Table 1 (left)
  if (config.lastPage.govSpecific) {
    const table1Body = [
      [
        {
          content:
            "في .................................\n..............................\n(الاسم, اللقب, الامضاء, الطابع)",
          styles: { halign: "right", valign: "middle" },
          rowSpan: 2,
        },
        {
          content: "عنوان المدرسة الإعدادية\nالموجه لها",
          styles: { halign: "right", valign: "middle" },
        },
      ],
      [
        {
          content:
            "..................................\n..................................",
          styles: { halign: "right", valign: "middle" },
        },
      ],
    ];

    doc.autoTable({
      head: [
        [
          {
            content: "خاص بالمندوبية الجهوية للتربية",
            colSpan: 2,
          },
        ],
      ],
      body: table1Body,
      startY: lastY,
      margin: { left: 10, bottom: 6 },
      pageBreak: "auto",
      styles: {
        font: "Amiri",
        fontStyle: "Bold",
        fillColor: [255, 255, 255],
        halign: "right",
        textDirection: "rtl",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        halign: "center",
        fillColor: [255, 255, 255], // white
        textColor: [0, 0, 0],
        lineWidth: 0.1,
        font: "Amiri",
        fontStyle: "Bold",
      },
      columnStyles: {
        0: { cellWidth: (pdf_width * 0.8) / 4 },
        1: { cellWidth: pdf_width / 4 },
      },
      theme: "grid",
    });
  }

  // Table 2 (right)
  if (config.lastPage?.managerAndTeacher?.allow) {
    const teachers = config.lastPage?.managerAndTeacher?.teacherList || [" "];
    if (config.lastPage?.rect) {
      const tableData = [];
      for (let i = 0; i < teachers.length; i++) {
        if (i == 0)
          tableData.push([
            { content: "", rowSpan: 1 },
            { content: "الإمضاء", rowSpan: 1 },
            { content: teachers[i], rowSpan: 1 },
            { content: "الاسم واللقب", rowSpan: 1 },
            {
              content: "المعلمون",
              rowSpan: teachers.length,
              styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
            },
          ]);
        else
          tableData.push([
            { content: "", rowSpan: 1 },
            { content: "الإمضاء", rowSpan: 1 },
            { content: teachers[i], rowSpan: 1 },
            { content: "الاسم واللقب", rowSpan: 1 },
            { content: "", rowSpan: 1 },
          ]);
      }
      tableData.push([
        { content: "", rowSpan: 1 },
        { content: "الإمضاء", rowSpan: 1 },
        {
          content: config.lastPage?.managerAndTeacher?.manager || " ",
          rowSpan: 1,
        },
        { content: "الاسم واللقب", rowSpan: 1 },
        {
          content: "مدير المدرسة",
          rowSpan: 1,
          styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
        },
      ]);

      doc.autoTable({
        head: [],
        body: tableData,
        startY: lastY,
        margin: { left: (pdf_width / 2) * 1.05, bottom: 6 },
        pageBreak: "auto",
        rowPageBreak: "auto",
        styles: {
          font: "Amiri",
          fontStyle: "normal",
          fillColor: [255, 255, 255],
          halign: "right",
          textDirection: "rtl",
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: (pdf_width / 2) * 0.175 },
          1: { cellWidth: (pdf_width / 2) * 0.175 },
          2: { cellWidth: (pdf_width / 2) * 0.175 },
          3: { cellWidth: (pdf_width / 2) * 0.175 },
          4: { cellWidth: (pdf_width / 2) * 0.175 },
        },
        theme: "grid",
      });
    } else {
      let rightText = " :الاسم واللّقب والإمضاء"
      let rightWidth = doc.getTextWidth(rightText);
      let rightX = pdf_width - 5 - rightWidth
      let y = lastY + 5;
      doc.text(rightText, rightX, y)
      y += 7;

      rightText = " : مدير المدرسة ";
      rightWidth = doc.getTextWidth(rightText);
      rightX = pdf_width - rightWidth - 10
      doc.text(rightText, rightX, y);

      let leftText = (config.lastPage?.managerAndTeacher?.manager || " ");
      let leftWidth = doc.getTextWidth(leftText);
      let leftX = pdf_width - rightWidth - leftWidth - 10 - 2
      doc.text(leftText, leftX, y);
      y += 7;

      for (let i = 0; i < teachers.length; i++) {
        const teacher = teachers[i];

        rightText = ":(السيّد )ة";
        rightWidth = doc.getTextWidth(rightText);
        rightX = pdf_width - rightWidth - 10
        doc.text(rightText, rightX, y);

        let leftText = (teacher || " ");
        let leftWidth = doc.getTextWidth(leftText);
        let leftX = pdf_width - rightWidth - leftWidth - 10 - 2
        doc.text(leftText, leftX, y);

        y += 5;
      }


    }

  }
  return doc;
}
async function printPvClass(config, data, info, studentsYearsLevel) {
  // The agePass section now follows config.footer.agePass.allow exactly; it is
  // no longer force-enabled when the class has age-pass ("P") students, so the
  // toggle can actually disable it.
  // Pre-render all rich-text blocks in parallel (warms the cache) so the
  // sequential PDF build below reuses ready canvases instead of rasterizing
  // each block one-by-one.
  //await prewarmRichText(config, data.level);
  let doc = new jsPDF({
    orientation: config.orientation ? "portrait" : "landscape",
  });
  const headers = await drawHeaders(doc, config, data, info);
  doc = headers.doc;
  let lastY = Math.max(headers.yLeft, headers.yRight, headers.yCenter);
  //if (config.footer?.exceptionalPass?.allow)
  [doc, lastY] = await drawTableAdmin(doc, data, config, sectionStartY(doc, lastY), studentsYearsLevel);

  console.log(config.footer)
  if (config.footer?.exceptionalPass?.allow)
    [doc, lastY] = await otherTables(
      doc,
      data,
      config.footer.exceptionalPass,
      sectionStartY(doc, lastY),
      "exceptionalPass"
    );
  if (config.footer?.agePass?.allow)
    [doc, lastY] = await otherTables(
      doc,
      data,
      config.footer.agePass,
      sectionStartY(doc, lastY),
      "agePass"
    );
  if (config.footer?.fail?.allow)
    [doc, lastY] = await otherTables(
      doc,
      data,
      config.footer.fail,
      sectionStartY(doc, lastY),
      "fail"
    );
  doc = await drawLastPage(doc, config, sectionStartY(doc, lastY, 3));
  return doc.output("bloburl");
}

export default printPvClass;
